import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Spinner } from "../components/Bits";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "long", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function EventCheckin() {
  const { tourId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", participants: 1 });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/events/${tourId}/info`)
      .then((r) => setEvent(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [tourId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Bitte gib deinen Namen ein");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/events/${tourId}/checkin`, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim(),
        participants: Number(form.participants) || 1,
      });
      setDone(r.data);
      toast.success("Du bist angemeldet!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="py-24"><Spinner /></div>;

  if (notFound) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="event-not-found">
        <div className="max-w-md text-center">
          <h1 className="font-display text-4xl mb-3" style={{ color: "var(--wp-olive)" }}>Event nicht gefunden</h1>
          <p className="text-sm opacity-70 mb-6">Bitte überprüfe den QR-Code oder frage am Stand.</p>
          <Link to="/" className="btn-primary inline-flex">Zur Startseite</Link>
        </div>
      </div>
    );
  }

  // Success view
  if (done) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-6" data-testid="event-checkin-success">
        <div className="max-w-md w-full text-center plant-card p-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-5 mx-auto" style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }}>
            <i className="fa-solid fa-circle-check text-4xl" />
          </div>
          <div className="overline mb-2">Erfolgreich angemeldet</div>
          <h1 className="font-display text-3xl mb-3" style={{ color: "var(--wp-olive)" }}>Willkommen, {done.name.split(" ")[0]}!</h1>
          <p className="text-sm leading-relaxed mb-2" style={{ color: "var(--wp-text-muted)" }}>
            Du bist beim Event <strong>{event.title}</strong> angemeldet.
          </p>
          {event.partner_name && (
            <p className="text-xs opacity-70 mb-5">Veranstaltet mit {event.partner_name}</p>
          )}
          {event.event_hashtag && (
            <div className="text-xs opacity-60 mb-5">
              Teil's auf Social Media: <span style={{ color: "var(--wp-olive)" }}>{event.event_hashtag.startsWith("#") ? event.event_hashtag : `#${event.event_hashtag}`}</span>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <Link to="/tours" className="btn-secondary justify-center">Weitere Touren ansehen</Link>
            <Link to="/" className="text-xs opacity-60 underline">Zur Startseite</Link>
          </div>
        </div>
      </div>
    );
  }

  const spotsLeft = event.max_participants ? (event.max_participants - (event.current_bookings || 0)) : null;
  const isFull = spotsLeft !== null && spotsLeft <= 0;

  return (
    <div className="min-h-[80vh] py-8 px-4 flex items-start justify-center" style={{ backgroundColor: "var(--wp-cream)" }} data-testid="event-checkin">
      <div className="max-w-md w-full">
        {/* Event header */}
        {event.image_url && (
          <div className="rounded-2xl overflow-hidden mb-5 shadow-lg">
            <img src={event.image_url} alt={event.title} className="w-full h-48 object-cover" />
          </div>
        )}
        {event.partner_logo_url && (
          <div className="flex items-center gap-3 mb-3">
            <img src={event.partner_logo_url} alt={event.partner_name} className="h-10 w-auto rounded" />
            {event.partner_name && <span className="text-xs opacity-70">in Kooperation mit {event.partner_name}</span>}
          </div>
        )}
        <div className="overline mb-2">Event-Anmeldung</div>
        <h1 className="font-display text-3xl md:text-4xl mb-2 leading-tight" style={{ color: "var(--wp-olive)" }}>{event.title}</h1>
        <div className="text-sm opacity-75 mb-1">
          <i className="fa-solid fa-calendar text-xs mr-2" /> {fmtDate(event.date)}
        </div>
        {event.meeting_point && (
          <div className="text-sm opacity-75 mb-4">
            <i className="fa-solid fa-location-dot text-xs mr-2" /> {event.meeting_point}
          </div>
        )}
        {event.description && (
          <p className="text-sm leading-relaxed mb-5" style={{ color: "var(--wp-text-muted)" }}>{event.description}</p>
        )}

        {/* Form */}
        {isFull ? (
          <div className="plant-card p-6 text-center" data-testid="event-full">
            <i className="fa-solid fa-circle-xmark text-3xl mb-3" style={{ color: "var(--wp-toxic, #b94a48)" }} />
            <h3 className="font-display text-xl mb-2">Event ausgebucht</h3>
            <p className="text-xs opacity-70">Schade! Frag am Stand, ob noch was geht.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="plant-card p-5 md:p-6 space-y-3" data-testid="event-checkin-form">
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-medium">Schnell anmelden</div>
              {spotsLeft !== null && spotsLeft <= 5 && (
                <div className="text-xs" style={{ color: "var(--wp-olive)" }}>
                  Noch {spotsLeft} {spotsLeft === 1 ? "Platz" : "Plätze"}
                </div>
              )}
            </div>
            <label className="block">
              <div className="overline mb-1">Name *</div>
              <input
                required
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border text-base"
                style={{ borderColor: "var(--wp-border)" }}
                placeholder="Vor- und Nachname"
                data-testid="event-form-name"
              />
            </label>
            <label className="block">
              <div className="overline mb-1">E-Mail (optional, für Bestätigung)</div>
              <input
                type="email"
                inputMode="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border text-base"
                style={{ borderColor: "var(--wp-border)" }}
                placeholder="du@email.com"
                data-testid="event-form-email"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="overline mb-1">Telefon (optional)</div>
                <input
                  type="tel"
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border text-base"
                  style={{ borderColor: "var(--wp-border)" }}
                  data-testid="event-form-phone"
                />
              </label>
              <label className="block">
                <div className="overline mb-1">Personen</div>
                <input
                  type="number"
                  min="1"
                  max={spotsLeft || 20}
                  value={form.participants}
                  onChange={(e) => setForm({ ...form, participants: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-3 rounded-xl border text-base"
                  style={{ borderColor: "var(--wp-border)" }}
                  data-testid="event-form-participants"
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center !py-4 text-base mt-2"
              data-testid="event-form-submit"
            >
              {busy ? "Melde an …" : "✓ Jetzt anmelden"}
            </button>
            <p className="text-[11px] opacity-60 text-center">
              Mit der Anmeldung stimmst du der Verarbeitung deiner Daten gemäß DSGVO zu.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
